import React, { useEffect, useMemo, useState } from "react";
import {
  Spin,
  Divider,
  Table,
  Tag,
  Alert,
  Button,
  Form,
  Input,
  Select,
  Modal,
  Popconfirm,
  Radio,
} from "antd";
import { faUsers, faUserShield } from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
// ROLE TEMPLATES
import { ROLE_TEMPLATES, RoleTemplate, buildRoleMenu } from "./roleTemplates";
// COMPONENTS
import DrawerRowDivider from "../../components/drawer/DrawerRowDivider";
// AUTH
import GETJwtToken from "../../../redux/authentication/authentication";
// PERMISSION CATALOGUE — the tree every role template is cut from
import useGetPermissionCatalogue from "../../../customHooks/GET_requests/useGetPermissionCatalogue";
// CONTEXT
import { useWizard } from "../WizardContext";
import { WizardOpGate, useCurrentWizardStepAccess } from "../WizardStepGate";
// WIZARD UTILS
import { WizardStep } from "../utils/wizardInvalidation";
import { useWizardStepDraft } from "../utils/useWizardStepDraft";
import { describeError } from "../utils/describeError";
// STORE
import { RootState } from "../../../app/store";
// RTK QUERY
import {
  useLazyGetRolesLightQuery,
  useLazyGetRoleQuery,
  useLazyGetRolesByOrganizationQuery,
  usePostRoleMutation,
  usePutRoleMutation,
  useDeleteRoleMutation,
} from "../../../redux/roles/roleAPI";
import {
  useLazyGetUsersByOrganizationQuery,
  usePostUserMutation,
  usePutUserMutation,
} from "../../../redux/users/userAPI";
// FLATBUFFERS
import { formatUuidT } from "../../../flatbuffers/display";
import { isMasterRoleName } from "../../../utils/isMasterAdmin";
// MASTER-ORG ROLE MARKER — shared ownership check + shared star marker
import { isMasterOrgId } from "../../../utils/masterOrganization";
import MasterRoleStar from "../../../components/roles/MasterRoleStar";
// TENANT ROLE MARKER — counterpart to MasterRoleStar: every role that is NOT
// a LiftNet/MASTER role gets this shield instead, so the Roles table and the
// user "Role" picker always show one marker or the other.
import TenantRoleShield from "../../../components/roles/TenantRoleShield";
import { UserStatus } from "../../../flatbuffers/generated/archive/user-status";
import { toUserStatus } from "../../../utils/userStatus";
import { userStatusLabel } from "../../../flatbuffers/display/enums";
// USER STATUS LEGEND — one shared source for the colour AND the meaning
import { userStatusColor } from "../../../components/users/userStatusMeta";
import UserStatusLegend from "../../../components/users/UserStatusLegend";
import RoleMarkerLegend from "../../../components/roles/RoleMarkerLegend";
import OfflinePasswordHandoff from "../../../components/users/OfflinePasswordHandoff";
// SHARED USER CRUD — the same actions menu and dialogs Organization Management
// uses, so both screens are one CRUD rather than two that resemble each other.
import UserCrudSurface from "../../../components/users/UserCrudSurface";
import UsersTableActions from "../../userManagement/users/UsersTableActions";
import { RoleMenuT } from "../../../flatbuffers/generated/archive/role-menu";
// ROLE MENU HELPERS — typed RoleMenuT model; JSON only at the REST boundary
import {
  PermissionGrantsJson,
  RoleMenuJson,
  duplicateRoleMenuT,
  editorCeilingMenu,
  menuJsonToRoleMenuT,
  readRolePermissionGrants,
  roleMenuSaveJson,
  roleMenuTToMenuJson,
  safeParseRoleMenu,
} from "../utils/roleMenu";
// SCOPE GRANTS
import { extractRoleScopeGrants } from "../../../redux/scope/extractRoleScopeGrants";
import { grantsToBodyArrays } from "../../../redux/scope/scopeSerialization";
import type { RoleScopeGrants } from "../../../redux/scope/scopeSlice";
import RoleScopeEditor from "../../../components/scopeEditor/RoleScopeEditor";
// WIZARD COMPONENTS
import WizardExportButton from "../components/WizardExportButton";
import WizardPermissionTreeEditor from "../components/WizardPermissionTreeEditor";
import WizardReadOnlyId from "../WizardReadOnlyId";

// Master-role guard shares isMasterRoleName so the wizard tracks the
// exact env + backend-enum resolution (the backend enforces the same
// guard server-side — hiding the buttons just keeps the UI honest).

/**
 * Build the contact_info array the users API expects from the form values.
 *
 * Organization Management has always sent one entry with a phone and a dialling
 * code; the wizard sent an empty array, so a user created here came out with no
 * contact record at all and the other screen showed blank fields for a person it
 * had not created. Same shape, one place, so the two forms agree.
 */
function contactInfoFrom(values: any): any[] {
  const phone = String(values.phone ?? "").trim();
  const phoneCode = String(values.phone_code ?? "").trim();
  if (phone === "" && phoneCode === "") return [];

  return [{ phone: phone || "1", phone_code: phoneCode }];
}

/** Normalize a value that may be a UuidT object to a UUID string. */
function uid(v: unknown): string {
  if (v == null) return "";
  if (typeof v === "string") return v;
  if (typeof v === "object") return formatUuidT(v as any) || String(v);
  return String(v);
}

// Roles owned by the master org (isMasterOrgId) are LiftNet-level templates
// shared with every tenant: GET /roles/?filter_target=organization_id unions
// the master org's own roles into every org's list, so they show up in this
// table for tenants that must not touch them. They are read-only here — the
// backend enforces the same guard, so hiding the actions keeps the UI honest
// instead of surfacing a guaranteed 403.

interface RoleRow {
  key: string;
  id: string;
  name: string;
  active: boolean;
  usersCount: number;
  /** Owning organization id — drives the master-org (LiftNet) read-only guard. */
  orgId: string;
  /** Convenience flag: the role belongs to the master organization. */
  isMasterOrgRole: boolean;
}

interface UserRow {
  key: string;
  id: string;
  firstName: string;
  lastName: string;
  name: string;
  email: string;
  roleId: string;
  roleName: string;
  status: UserStatus;
  /**
   * The user's linked Cognito identity, straight from Postgres via
   * Archive.User.cognito_id -- no frontend transformation. Genuinely empty
   * for many rows by design (new/invited/offline/blocked/deleted users can
   * have cognito_id = NULL server-side), not a decode failure; that's real
   * information, not a bug, so it's displayed as-is rather than hidden.
   */
  cognitoId: string;
  /**
   * The user exactly as the API returned it. UsersTableActions and the dialogs
   * it opens read the API shape, not this mapped row, so the row carries the
   * original rather than each dialog learning a second shape.
   */
  raw: any;
}

interface BaseRoleOption {
  id: string;
  name: string;
  orgName: string;
}

export default function StepUsersRoles() {
  const [t] = useTranslation("global");
  const { theme, userLogged } = useSelector((state: RootState) => state.home);
  const { state: wizardState, markStepCompleted, setState: setWizardState } = useWizard();
  const access = useCurrentWizardStepAccess();
  // Only a user who themselves belongs to the master organization may see the
  // MASTER ADMIN role at all here — same guard RolesTable.tsx uses. Everyone
  // else gets that row hidden entirely (Roles table + the Add User "Role"
  // picker), not just gated behind an action, since the role has no
  // relevance outside LiftNet.
  const isLoggedUserInMasterOrg =
    userLogged?.organizationId === process.env.REACT_APP_MASTER_ORG_ID;

  // ************************************************ */
  // LOCAL STATE ************************************ */
  const [roles, setRoles] = useState<RoleRow[]>([]);
  const [users, setUsers] = useState<UserRow[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);

  // Add Role modal state
  const [addRoleOpen, setAddRoleOpen] = useState(false);
  const [addRoleSaving, setAddRoleSaving] = useState(false);
  const [addRoleError, setAddRoleError] = useState<string | null>(null);
  const [baseRoles, setBaseRoles] = useState<BaseRoleOption[]>([]);
  // The one-time offline password from the create response. Held only until
  // the hand-off dialog is dismissed — never logged, stored or put in Redux,
  // because this is a live credential.
  const [handoffPassword, setHandoffPassword] = useState("");
  const [handoffUserLabel, setHandoffUserLabel] = useState("");
  const [roleForm] = Form.useForm();

  // Create-from-template modal state. The wizard's "Add Role" path
  // requires picking an existing base role — when an org doesn't have
  // any base roles yet (fresh tenant), or when the operator wants a
  // brand-new role with one of the prebuilt permission profiles
  // (Master Admin, Admin, View+Control, View Only), they use this
  // modal instead. It produces a fresh role via POST /roles/ using
  // buildRoleMenu(template) as the permission menu, with a custom
  // name and optional display-name alias.
  const [createRoleOpen, setCreateRoleOpen] = useState(false);
  const [createRoleSaving, setCreateRoleSaving] = useState(false);
  const [createRoleError, setCreateRoleError] = useState<string | null>(null);
  const [createRoleForm] = Form.useForm();

  // Add / Edit User modal state
  const [addUserOpen, setAddUserOpen] = useState(false);
  const [addUserSaving, setAddUserSaving] = useState(false);
  const [addUserError, setAddUserError] = useState<string | null>(null);
  const [editingUserId, setEditingUserId] = useState<string | null>(null);
  const [editingUserCognitoId, setEditingUserCognitoId] = useState<string | null>(null);
  const [userForm] = Form.useForm();

  // Edit Role modal state. The wizard previously only supported creating
  // roles (from a base or from a template). Operators now need to rename
  // or toggle existing roles without leaving the wizard, so we surface a
  // lightweight Edit modal that PUTs name / display_name / active and
  // preserves the existing menu/permissionOverrides untouched.
  const [editRoleOpen, setEditRoleOpen] = useState(false);
  const [editRoleSaving, setEditRoleSaving] = useState(false);
  const [editRoleError, setEditRoleError] = useState<string | null>(null);
  const [editingRoleId, setEditingRoleId] = useState<string | null>(null);
  const [editRoleForm] = Form.useForm();

  // Inline permission editing (typed RoleMenuT models, one per modal).
  // addRoleMenu mirrors the selected base role; createRoleMenu mirrors
  // the chosen template; editRoleMenu is the fetched role's live menu.
  const [addRoleMenu, setAddRoleMenu] = useState<RoleMenuT | null>(null);
  // Which base role the loaded menu belongs to — guards against a slow
  // fetch for role A resolving after the operator switched to role B
  // (stale menu silently submitted with B's name).
  const [addRoleMenuForId, setAddRoleMenuForId] = useState<string | null>(null);
  const [addRoleMenuLoading, setAddRoleMenuLoading] = useState(false);
  const [createRoleMenu, setCreateRoleMenu] = useState<RoleMenuT | null>(null);
  // The templates are cut from what the signed-in user may grant: their own
  // role's menu with un-held permissions pruned (editorCeilingMenu, as in the
  // roles form), falling back to the server's permission catalogue. LiftCloud
  // refuses a role anything beyond its creator's own permissions, so a
  // template offering more would only fail on save. The Create-New-Role modal
  // cannot open until that ceiling is known.
  const { catalogueMenu, isErrorPermissionCatalogue } = useGetPermissionCatalogue();
  const templateCeiling = useMemo(() => {
    const ceiling = editorCeilingMenu(userLogged?.role?.menu, catalogueMenu);
    return ceiling ? roleMenuTToMenuJson(ceiling) : null;
  }, [userLogged, catalogueMenu]);
  const templateMenu = (template: RoleTemplate): RoleMenuT | null =>
    templateCeiling
      ? menuJsonToRoleMenuT(buildRoleMenu(template, templateCeiling) as RoleMenuJson)
      : null;
  // The edited role's explicit decisions before the edit, so saving keeps
  // those on keys the inline editor does not show.
  const [editRoleExistingGrants, setEditRoleExistingGrants] = useState<PermissionGrantsJson>({});
  const [editRoleMenu, setEditRoleMenu] = useState<RoleMenuT | null>(null);
  const [editRoleMenuLoading, setEditRoleMenuLoading] = useState(false);
  const [duplicatingRoleId, setDuplicatingRoleId] = useState<string | null>(null);

  // Per-bank/lift/floor scope grants, one state object per role modal.
  const EMPTY_SCOPE: RoleScopeGrants = { bankGrants: [], liftGrants: [], floorGrants: [] };
  const [addRoleScope, setAddRoleScope] = useState<RoleScopeGrants>(EMPTY_SCOPE);
  const [createRoleScope, setCreateRoleScope] = useState<RoleScopeGrants>(EMPTY_SCOPE);
  const [editRoleScope, setEditRoleScope] = useState<RoleScopeGrants>(EMPTY_SCOPE);

  // ************************************************ */
  // DRAFT PERSISTENCE ****************************** */
  // Roles and users are saved to the server immediately on modal submit
  // (no half-saved state lingers in React after a successful submit).
  // The only transient operator context worth restoring is *which row
  // they were editing* — if a reload happens mid-edit, returning the
  // operator to the same user's edit modal beats losing the context.
  //
  // Verification: editingUserId is only restored when the referenced
  // user still exists in the loaded users list (the data fetch above
  // runs before our restore via `loaded`).
  useWizardStepDraft<{ editingUserId: string | null }>({
    orgId: wizardState.organizationId,
    step: WizardStep.Users,
    scopeId: "users-roles",
    scopeType: "step",
    draft: { editingUserId },
    onRestore: (saved) => {
      if (
        typeof saved.editingUserId === "string" &&
        users.some((u) => u.id === saved.editingUserId)
      ) {
        setEditingUserId(saved.editingUserId);
      }
    },
  });

  // ************************************************ */
  // RTK QUERY HOOKS ******************************* */
  const [triggerGetRolesLight] = useLazyGetRolesLightQuery();
  const [triggerGetRole] = useLazyGetRoleQuery();
  const [triggerGetRolesByOrg] = useLazyGetRolesByOrganizationQuery();
  const [triggerGetUsersByOrg] = useLazyGetUsersByOrganizationQuery();
  const [triggerPostRole] = usePostRoleMutation();
  const [triggerPutRole] = usePutRoleMutation();
  const [triggerDeleteRole] = useDeleteRoleMutation();
  const [triggerPostUser] = usePostUserMutation();
  const [triggerPutUser] = usePutUserMutation();

  // ************************************************ */
  // USE EFFECTS *********************************** */
  useEffect(() => {
    if (!wizardState.organizationId || loaded) return;

    const fetchData = async () => {
      setFetching(true);
      try {
        const token = await GETJwtToken();
        const orgId = wizardState.organizationId as string;

        // Use RTK Query which goes through flatBufferBaseQuery for proper
        // FlatBuffer response decoding.
        const [rolesResult, usersResult] = await Promise.allSettled([
          triggerGetRolesByOrg({ orgId, token }).unwrap(),
          triggerGetUsersByOrg({ orgId, token, page: 1, limit: 9999 }).unwrap(),
        ]);

        let rolesData: any[] = [];
        let usersData: any[] = [];

        if (rolesResult.status === "fulfilled") {
          rolesData = rolesResult.value?.data || [];
        }

        if (usersResult.status === "fulfilled") {
          usersData = usersResult.value?.data || [];
        }

        // Build a role ID -> name lookup for resolving user role names
        const roleMap: Record<string, string> = {};
        rolesData.forEach((r: any) => {
          const rid = uid(r.id);
          roleMap[rid] = r.name || r.displayName || rid;
        });

        // Count users per role
        const roleUserCounts: Record<string, number> = {};
        usersData.forEach((u: any) => {
          const rid = uid(u.role_id ?? u.roleId);
          if (rid) {
            roleUserCounts[rid] = (roleUserCounts[rid] || 0) + 1;
          }
        });

        const roleRows: RoleRow[] = rolesData.map((r: any) => {
          const rid = uid(r.id);
          const roleOrgId = uid(r.org_id ?? r.orgId);
          return {
            key: rid,
            id: rid,
            name: r.name || r.displayName || rid,
            active: r.active !== false,
            usersCount: roleUserCounts[rid] || 0,
            orgId: roleOrgId,
            isMasterOrgRole: isMasterOrgId(roleOrgId),
          };
        });

        const userRows: UserRow[] = usersData.map((u: any) => {
          const uId = uid(u.id);
          const firstName: string = u.first_name ?? u.firstName ?? "";
          const lastName: string = u.last_name ?? u.lastName ?? "";
          const fullName = `${firstName} ${lastName}`.trim() || u.email || uId;
          const userRoleId = uid(u.role_id ?? u.roleId);
          return {
            key: uId,
            id: uId,
            firstName,
            lastName,
            name: fullName,
            email: u.email || "",
            roleId: userRoleId,
            roleName: userRoleId ? roleMap[userRoleId] || userRoleId : "",
            status: toUserStatus(u.status),
            cognitoId: uid(u.cognito_id ?? u.cognitoId),
            raw: u,
          };
        });

        setRoles(roleRows);
        setUsers(userRows);

        // Drop wizard-tracked ids the organization no longer has. Deleting a
        // user used to prune this list at the moment of deletion, which only
        // worked while this step owned the delete; the shared actions menu can
        // now delete from here or from Organization Management. Reconciling
        // against what the server actually returned covers both, and heals a
        // list that drifted for any other reason too.
        const presentIds = new Set(userRows.map((row) => row.id));
        setWizardState((prev) =>
          prev.userIds.every((id) => presentIds.has(id))
            ? prev
            : { ...prev, userIds: prev.userIds.filter((id) => presentIds.has(id)) },
        );

        setLoaded(true);

        // Auto-complete: this step is informational, mark completed once loaded.
        // The hardcoded `markStepCompleted(8)` here previously referred to the
        // OLD step indices and accidentally marked BACnet complete instead of
        // Users when ChannelModifiers was inserted at index 5.
        markStepCompleted(WizardStep.Users);
      } catch (err: any) {
        setFetchError(`Failed to load users and roles — ${describeError(err)}`);
      } finally {
        setFetching(false);
      }
    };

    fetchData();
  }, [wizardState.organizationId, loaded]);

  // ************************************************ */
  // HANDLERS ************************************** */

  /** Fetch available base roles when the Add Role modal opens */
  const handleOpenAddRole = async () => {
    setAddRoleOpen(true);
    setAddRoleError(null);
    setAddRoleMenu(null);
    setAddRoleScope(EMPTY_SCOPE);
    try {
      const token = await GETJwtToken();
      const orgId = wizardState.organizationId as string;
      const result = await triggerGetRolesLight({ orgId, token }).unwrap();
      const rolesLight = result?.data || result || [];
      const options: BaseRoleOption[] = (Array.isArray(rolesLight) ? rolesLight : []).map(
        (r: any) => ({
          id: uid(r.id),
          name: r.name || uid(r.id),
          orgName: typeof r.organization === "object" ? r.organization?.name || "" : "",
        }),
      );
      setBaseRoles(options);
    } catch {
      setAddRoleError("Failed to load available roles.");
    }
  };

  /**
   * Fetch a role's full menu and return it as a typed RoleMenuT.
   * Shared by the base-role picker, the edit modal, and duplication.
   */
  const fetchRoleMenu = async (roleId: string): Promise<RoleMenuT | null> => {
    const token = await GETJwtToken();
    // preferCacheValue lets RTK Query serve a fresh cache hit (e.g. Edit
    // then Duplicate on the same role) instead of refetching back-to-back.
    const roleResult = await triggerGetRole({ roleId, token }, true).unwrap();
    const role = roleResult?.data || roleResult;
    return safeParseRoleMenu(role?.menu ?? role?.menuJson);
  };

  /**
   * Load the selected base role's permissions into the inline editor.
   * Latest-wins: results for a base role the operator has already
   * switched away from are discarded.
   */
  const handleBaseRoleSelected = async (baseRoleId: string) => {
    setAddRoleMenuLoading(true);
    setAddRoleError(null);
    setAddRoleMenuForId(baseRoleId);
    setAddRoleMenu(null);
    try {
      const fetched = await fetchRoleMenu(baseRoleId);
      setAddRoleMenuForId((currentId) => {
        if (currentId === baseRoleId) {
          setAddRoleMenu(fetched);
          setAddRoleMenuLoading(false);
        }
        return currentId;
      });
    } catch {
      setAddRoleMenuForId((currentId) => {
        if (currentId === baseRoleId) {
          setAddRoleError("Failed to load the base role's permissions.");
          setAddRoleMenu(null);
          setAddRoleMenuLoading(false);
        }
        return currentId;
      });
    }
  };

  /** Create a new role for this org based on a selected base role */
  const handleAddRole = async (values: { base_role_id: string; display_name?: string }) => {
    setAddRoleSaving(true);
    setAddRoleError(null);
    try {
      const token = await GETJwtToken();
      const orgId = wizardState.organizationId as string;

      const baseRoleName = baseRoles.find((r) => r.id === values.base_role_id)?.name || "Role";
      // Prefer the inline editor's (possibly customized) menu — but only
      // when it belongs to the SUBMITTED base role; otherwise fetch
      // fresh (covers never-loaded, still-loading, and stale-after-
      // switch states alike).
      const editedMenuMatches = addRoleMenu && addRoleMenuForId === values.base_role_id;
      const sourceMenu = editedMenuMatches ? addRoleMenu : await fetchRoleMenu(values.base_role_id);
      if (!sourceMenu) {
        setAddRoleError("The base role's permission menu could not be read.");
        setAddRoleSaving(false);
        return;
      }
      // The request serializer calls JSON.stringify(body.menu), so pass
      // an object (snake_case via roleMenuSaveJson) — never a string. Every
      // node's switches ride along as explicit decisions.
      const menuObj = roleMenuSaveJson(sourceMenu);

      const roleName = values.display_name?.trim() || baseRoleName;
      menuObj.name = roleName;

      const result = await triggerPostRole({
        token,
        body: {
          active: true,
          offline: true,
          name: roleName,
          display_name: values.display_name?.trim() || undefined,
          base_role_id: values.base_role_id,
          org_id: orgId,
          menu: menuObj,
          ...grantsToBodyArrays(addRoleScope),
        },
      }).unwrap();

      const created = result?.data || result;
      const newId = uid(created?.id) || "";
      const newName = created?.name || roleName;

      setRoles((prev) => [
        ...prev,
        {
          key: newId,
          id: newId,
          name: newName,
          active: true,
          usersCount: 0,
          // Roles created from the wizard always belong to the org being
          // configured, never to the master org.
          orgId,
          isMasterOrgRole: isMasterOrgId(orgId),
        },
      ]);

      roleForm.resetFields();
      setAddRoleScope(EMPTY_SCOPE);
      setAddRoleOpen(false);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      setAddRoleError(msg || "Failed to create role.");
    } finally {
      setAddRoleSaving(false);
    }
  };

  /**
   * Create a brand-new role for this org from a built-in template
   * (Master Admin / Admin / View+Control / View Only). The selected
   * template determines the permission menu via buildRoleMenu(template),
   * which is sent through the FlatBuffer-backed POST /roles/ — never
   * JSON.stringify'd at this layer (the request serializer handles
   * encoding). The new role is appended to both the visible roles
   * table and the baseRoles list so it's immediately selectable in the
   * Add Role modal without a refetch.
   */
  const handleCreateRoleFromTemplate = async (values: {
    name: string;
    display_name?: string;
    template: RoleTemplate;
  }) => {
    setCreateRoleSaving(true);
    setCreateRoleError(null);
    try {
      const token = await GETJwtToken();
      const orgId = wizardState.organizationId as string;

      // Prefer the inline editor's (possibly customized) tree; fall back
      // to the raw template when the editor never initialized.
      const sourceMenu = createRoleMenu ?? templateMenu(values.template);
      if (!sourceMenu) {
        setCreateRoleError("The permission catalogue has not loaded yet.");
        return;
      }
      const menuObj = roleMenuSaveJson(sourceMenu);
      // Stamp the user-chosen name onto the menu so backends that key
      // off menu.name remain consistent with the role record name.
      menuObj.name = values.name;

      const result = await triggerPostRole({
        token,
        body: {
          active: true,
          offline: true,
          name: values.name,
          display_name: values.display_name?.trim() || undefined,
          org_id: orgId,
          menu: menuObj,
          ...grantsToBodyArrays(createRoleScope),
        },
      }).unwrap();

      const created = result?.data || result;
      const newId = uid(created?.id) || "";
      const newName = created?.name || values.name;

      setRoles((prev) => [
        ...prev,
        {
          key: newId,
          id: newId,
          name: newName,
          active: true,
          usersCount: 0,
          // Roles created from the wizard always belong to the org being
          // configured, never to the master org.
          orgId,
          isMasterOrgRole: isMasterOrgId(orgId),
        },
      ]);
      // Make this role immediately selectable as a base in the Add
      // Role modal without re-fetching the light role list.
      setBaseRoles((prev) => [...prev, { id: newId, name: newName, orgName: "" }]);

      createRoleForm.resetFields();
      setCreateRoleScope(EMPTY_SCOPE);
      setCreateRoleOpen(false);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      setCreateRoleError(msg || "Failed to create role from template.");
    } finally {
      setCreateRoleSaving(false);
    }
  };

  /** Create or update a user for this org */
  const handleAddUser = async (values: {
    first_name: string;
    middle_name?: string;
    last_name: string;
    email: string;
    role_id: string;
    phone?: string;
    phone_code?: string;
  }) => {
    setAddUserSaving(true);
    setAddUserError(null);
    try {
      const token = await GETJwtToken();
      const roleName = roles.find((r) => r.id === values.role_id)?.name ?? values.role_id;

      if (editingUserId) {
        // Edit mode — PUT
        await triggerPutUser({
          id: editingUserId,
          token,
          body: {
            email: values.email,
            first_name: values.first_name || "",
            middle_name: values.middle_name || "",
            last_name: values.last_name || "",
            role_id: values.role_id,
            org_id: wizardState.organizationId,
            contact_info: contactInfoFrom(values),
          },
        }).unwrap();
        const editFirstName = values.first_name || "";
        const editLastName = values.last_name || "";
        const fullName = `${editFirstName} ${editLastName}`.trim() || values.email;
        setUsers((prev) =>
          prev.map((u) =>
            u.id === editingUserId
              ? {
                  ...u,
                  firstName: editFirstName,
                  lastName: editLastName,
                  name: fullName,
                  email: values.email,
                  roleId: values.role_id,
                  roleName,
                }
              : u,
          ),
        );
        setEditingUserId(null);
      } else {
        // Add mode — POST through the RTK Query / FlatBuffer pipeline.
        //
        // The previous implementation called `fetch()` directly with a
        // hand-encoded JSON body, which bypassed `createFlatBufferBaseQuery`
        // and the whole serialization layer the rest of the wizard relies
        // on. That's the "Zero JSON internal" violation the team flagged:
        // every internal write should ride the FB transport so request
        // serializers (camelCase → FBT pack) and response decoders
        // (FB binary → JS object) stay consistent across the app.
        // `usePostUserMutation` already lives in userAPI.ts behind
        // createFlatBufferBaseQuery, so swapping fetch out is the
        // minimal fix.
        const postResult = await triggerPostUser({
          token,
          body: {
            email: values.email,
            first_name: values.first_name || "",
            middle_name: values.middle_name || "",
            last_name: values.last_name || "",
            org_id: wizardState.organizationId,
            role_id: values.role_id,
            contact_info: contactInfoFrom(values),
            command_notifications: true,
            global_notifications: true,
          },
        }).unwrap();
        // FB-decoded response unwraps to either { data: {...} } or the
        // user object directly, depending on the registered schema; keep
        // the same defensive `.data || ...` pattern the prior fetch path
        // used so callers don't have to care which shape arrives.
        // `userPassword` rides on CreateUserResponse, the schema POST /users
        // answers with. An edge server mints it; a cloud server returns an
        // empty string because it creates a directory identity instead, so an
        // empty value here means "nothing to hand over", not "lost".
        const generatedPassword = String((postResult as any)?.userPassword ?? "");
        const u: any = (postResult as any)?.data || postResult;
        // FB-decoded responses return UuidT objects for id fields;
        // normalize via the local `uid` helper so the rest of the
        // wizard (which keys by plain string ids) keeps working.
        const newUserId = uid(u.id);
        const newFirstName: string = u.first_name || u.firstName || values.first_name || "";
        const newLastName: string = u.last_name || u.lastName || values.last_name || "";
        const fullName = `${newFirstName} ${newLastName}`.trim() || u.email || newUserId;
        setUsers((prev) => [
          ...prev,
          {
            key: newUserId,
            id: newUserId,
            firstName: newFirstName,
            lastName: newLastName,
            name: fullName,
            email: u.email || values.email,
            roleId: values.role_id,
            roleName,
            status: toUserStatus(u.status),
            // A brand-new user has no Cognito link yet -- that account gets
            // created separately, after this POST returns.
            cognitoId: uid(u.cognito_id ?? u.cognitoId),
            raw: u,
          },
        ]);
        setRoles((prev) =>
          prev.map((r) => (r.id === values.role_id ? { ...r, usersCount: r.usersCount + 1 } : r)),
        );

        // Hand-off is the last step of provisioning, not an optional extra:
        // the server stores only a hash, so this is the one moment the
        // password is readable by anyone.
        if (generatedPassword !== "") {
          setHandoffUserLabel(fullName);
          setHandoffPassword(generatedPassword);
        }
      }

      userForm.resetFields();
      setAddUserOpen(false);
    } catch (err: unknown) {
      const fallback = editingUserId ? "Failed to update user." : "Failed to create user.";
      setAddUserError(err instanceof Error ? err.message : fallback);
    } finally {
      setAddUserSaving(false);
    }
  };

  /**
   * Open the Edit Role modal pre-populated with the row's data and the
   * role's full permission menu (fetched fresh so the inline editor
   * starts from the server's truth, not a stale cache).
   */
  const handleOpenEditRole = async (role: RoleRow) => {
    setEditingRoleId(role.id);
    setEditRoleError(null);
    setEditRoleMenu(null);
    setEditRoleExistingGrants({});
    setEditRoleScope(EMPTY_SCOPE);
    editRoleForm.setFieldsValue({
      name: role.name,
      active: role.active,
    });
    setEditRoleOpen(true);
    setEditRoleMenuLoading(true);
    try {
      const token = await GETJwtToken();
      const roleResult = await triggerGetRole({ roleId: role.id, token }, true).unwrap();
      const roleData = roleResult?.data || roleResult;
      setEditRoleMenu(safeParseRoleMenu(roleData?.menu ?? roleData?.menuJson));
      setEditRoleExistingGrants(readRolePermissionGrants(roleData));
      setEditRoleScope(extractRoleScopeGrants(roleData));
    } catch {
      setEditRoleError("Failed to load the role's permission menu.");
    } finally {
      setEditRoleMenuLoading(false);
    }
  };

  /**
   * Update an existing role: name, active status, AND the permission
   * menu from the inline editor. The full menu is always sent — the
   * backend's RoleRequest binding requires it on every PUT, so the old
   * name-only payload 400'd on every save.
   */
  const handleEditRole = async (values: { name: string; active: boolean }) => {
    if (!editingRoleId) return;
    setEditRoleSaving(true);
    setEditRoleError(null);
    try {
      const token = await GETJwtToken();
      const orgId = wizardState.organizationId as string;
      // The inline editor's menu when loaded; otherwise re-fetch so a
      // failed initial load doesn't permanently block renames — the
      // unedited server menu rides along and only name/active change.
      const sourceMenu = editRoleMenu ?? (await fetchRoleMenu(editingRoleId));
      if (!sourceMenu) {
        setEditRoleError("The role's permission menu could not be read — try reopening.");
        setEditRoleSaving(false);
        return;
      }
      const menuObj = roleMenuSaveJson(sourceMenu, editRoleExistingGrants);
      menuObj.name = values.name;
      await triggerPutRole({
        id: editingRoleId,
        token,
        body: {
          org_id: orgId,
          name: values.name,
          display_name: values.name,
          active: values.active,
          menu: menuObj,
          ...grantsToBodyArrays(editRoleScope),
        },
      }).unwrap();
      setRoles((prev) =>
        prev.map((r) =>
          r.id === editingRoleId ? { ...r, name: values.name, active: values.active } : r,
        ),
      );
      editRoleForm.resetFields();
      setEditRoleOpen(false);
      setEditingRoleId(null);
      setEditRoleMenu(null);
      setEditRoleScope(EMPTY_SCOPE);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      setEditRoleError(msg || "Failed to update role.");
    } finally {
      setEditRoleSaving(false);
    }
  };

  /**
   * One-click duplicate: clone the source role's menu into a brand-new
   * editable role named "<source> (Copy)". The copy carries no
   * base_role_id so later edits to the source never bleed through.
   */
  const handleDuplicateRole = async (source: RoleRow) => {
    setDuplicatingRoleId(source.id);
    try {
      const token = await GETJwtToken();
      const orgId = wizardState.organizationId as string;
      // Fetch the full role record to clone both the menu AND scope grants.
      const roleResult = await triggerGetRole({ roleId: source.id, token }, true).unwrap();
      const roleData = roleResult?.data || roleResult;
      const sourceMenu = safeParseRoleMenu(roleData?.menu ?? roleData?.menuJson);
      if (!sourceMenu) {
        setFetchError("The source role's permission menu could not be read.");
        return;
      }
      const sourceScope = extractRoleScopeGrants(roleData);
      const copyName = `${source.name} (Copy)`;
      const menuObj = roleMenuSaveJson(
        duplicateRoleMenuT(sourceMenu),
        readRolePermissionGrants(roleData),
      );
      menuObj.name = copyName;
      const result = await triggerPostRole({
        token,
        body: {
          active: true,
          offline: true,
          name: copyName,
          org_id: orgId,
          menu: menuObj,
          ...grantsToBodyArrays(sourceScope),
        },
      }).unwrap();
      const created = result?.data || result;
      const newId = uid(created?.id) || "";
      const newName = created?.name || copyName;
      setRoles((prev) => [
        ...prev,
        {
          key: newId,
          id: newId,
          name: newName,
          active: true,
          usersCount: 0,
          orgId,
          isMasterOrgRole: isMasterOrgId(orgId),
        },
      ]);
      setBaseRoles((prev) => [...prev, { id: newId, name: newName, orgName: "" }]);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      setFetchError(msg || "Failed to duplicate role.");
    } finally {
      setDuplicatingRoleId(null);
    }
  };

  /**
   * Delete a role. The backend rejects deletion when users still
   * reference the role; we surface that error in-place via the existing
   * GlobalAlert2 path so operators understand why the row didn't go away.
   * The local optimistic remove only happens after the server confirms.
   */
  const handleDeleteRole = async (roleId: string) => {
    try {
      const token = await GETJwtToken();
      await triggerDeleteRole({ id: roleId, token }).unwrap();
      setRoles((prev) => prev.filter((r) => r.id !== roleId));
      setBaseRoles((prev) => prev.filter((r) => r.id !== roleId));
    } catch {
      // Error surfaced by GlobalAlert2 — keep the row visible so the
      // operator knows the delete didn't take effect.
    }
  };

  /** Open the user modal in edit mode pre-populated with the row's data */
  const handleEditUser = (user: UserRow) => {
    setEditingUserId(user.id);
    setEditingUserCognitoId(user.cognitoId);
    setAddUserError(null);
    const contact = Array.isArray(user.raw?.contact_info) ? user.raw.contact_info[0] : undefined;
    userForm.setFieldsValue({
      first_name: user.firstName,
      middle_name: user.raw?.middle_name ?? user.raw?.middleName ?? "",
      last_name: user.lastName,
      email: user.email,
      role_id: user.roleId,
      phone: contact?.phone ?? "",
      phone_code: contact?.phone_code ?? "",
    });
    setAddUserOpen(true);
  };

  // FRONTEND-ONLY visual filter (same trade-off as RolesTable.tsx): a user
  // outside the master organization never sees the MASTER ADMIN row in
  // either the Roles table or the Add User "Role" picker. Unlike
  // RolesTable.tsx this list is NOT server-paginated (fetched once, in
  // full, for the org), so there's no page-fill/total-count skew here — the
  // filter is exact.
  const visibleRoles = useMemo(
    () => (isLoggedUserInMasterOrg ? roles : roles.filter((role) => !isMasterRoleName(role.name))),
    [roles, isLoggedUserInMasterOrg],
  );

  // LiftNet/MASTER roles surface first everywhere `roles` is listed (the
  // Roles table and the Users "Role" picker) — Array#sort is stable, so
  // within each group (master / non-master) the fetch order is kept.
  const sortedRoles = useMemo(
    () => [...visibleRoles].sort((a, b) => Number(b.isMasterOrgRole) - Number(a.isMasterOrgRole)),
    [visibleRoles],
  );

  // ************************************************ */
  // TABLE COLUMNS ********************************* */
  const roleColumns = [
    {
      title: t("general.name"),
      dataIndex: "name",
      key: "name",
      render: (name: string, record: RoleRow) => (
        <span>
          {/* A star marks a MASTER role — one owned by the LiftNet master
              organization — so operators can tell the shared, read-only
              templates apart from their own org's roles at a glance. Every
              other role gets the tenant shield instead, so every row carries
              one marker or the other. */}
          <MasterRoleStar show={record.isMasterOrgRole} />
          <TenantRoleShield show={!record.isMasterOrgRole} />
          {name}
        </span>
      ),
    },
    {
      title: t("general.active"),
      dataIndex: "active",
      key: "active",
      render: (val: boolean) =>
        val ? <Tag color="green">Active</Tag> : <Tag color="default">Inactive</Tag>,
    },
    {
      title: "Users",
      dataIndex: "usersCount",
      key: "usersCount",
      render: (count: number) => count,
    },
    {
      title: "",
      key: "actions",
      width: 300,
      render: (_: unknown, record: RoleRow) => {
        // A role owned by the master organization (LiftNet) is read-only
        // from the wizard: no Edit, no Delete — the backend refuses those
        // writes anyway. Duplicate is still offered so operators can start a
        // new, tenant-owned role from a LiftNet template without leaving the
        // wizard.
        if (record.isMasterOrgRole) {
          return (
            <div style={{ display: "flex", gap: 4 }}>
              <WizardOpGate op="create">
                <Button
                  type="link"
                  size="small"
                  loading={duplicatingRoleId === record.id}
                  onClick={() => handleDuplicateRole(record)}
                >
                  Duplicate
                </Button>
              </WizardOpGate>
            </div>
          );
        }
        const isMasterRole = isMasterRoleName(record.name);
        return (
          <div style={{ display: "flex", gap: 4 }}>
            {/* The master role is immutable from the wizard — the backend
                enforces the same guard; hiding the buttons keeps the UI
                honest instead of surfacing a guaranteed 403. */}
            {!isMasterRole && (
              <WizardOpGate op="update">
                <Button type="link" size="small" onClick={() => handleOpenEditRole(record)}>
                  Edit
                </Button>
              </WizardOpGate>
            )}
            <WizardOpGate op="create">
              <Button
                type="link"
                size="small"
                loading={duplicatingRoleId === record.id}
                onClick={() => handleDuplicateRole(record)}
              >
                Duplicate
              </Button>
            </WizardOpGate>
            {!isMasterRole && (
              <WizardOpGate op="delete">
                <Popconfirm
                  title={
                    record.usersCount > 0
                      ? `This role has ${record.usersCount} user(s) assigned. The server will refuse until they are reassigned.`
                      : "Delete this role?"
                  }
                  onConfirm={() => handleDeleteRole(record.id)}
                  okText="Delete"
                  okButtonProps={{ danger: true }}
                >
                  <Button type="link" danger size="small">
                    Delete
                  </Button>
                </Popconfirm>
              </WizardOpGate>
            )}
          </div>
        );
      },
    },
  ];

  const userColumns = [
    {
      title: t("general.name"),
      dataIndex: "name",
      key: "name",
    },
    {
      title: t("general.email"),
      dataIndex: "email",
      key: "email",
    },
    {
      title: "Role",
      dataIndex: "roleName",
      key: "roleName",
      render: (val: string) => val || <em>None</em>,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (val: unknown) => {
        // Colour comes from the shared status map, so every one of the seven
        // statuses is distinguishable. The old three-branch chain painted the
        // other four the same default grey, which made an OFFLINE_ACTIVE user
        // and a DELETED one look alike. UserStatusLegend below reads the same
        // map, so the legend and the column can never disagree.
        const status = toUserStatus(val);
        return <Tag color={userStatusColor(status)}>{userStatusLabel(status)}</Tag>;
      },
    },
    {
      title: "",
      key: "actions",
      width: 140,
      // The SAME actions menu the Organization Management Users table renders,
      // so the two screens offer one set of user operations rather than two
      // that drift. This step used to show only Edit and Remove, which meant
      // the offline credential, the block/unblock and the password reset were
      // reachable from one screen and not the other — and "Remove" called the
      // very same DELETE the other screen calls "Delete".
      //
      // onEdit keeps Edit in place: the default navigates to Organization
      // Management, which would abandon the wizard mid-setup.
      render: (_: unknown, record: UserRow) => (
        <UsersTableActions selectedItem={record.raw} onEdit={() => handleEditUser(record)} />
      ),
    },
  ];

  // ************************************************ */
  // COMPONENT ************************************* */
  const isLoading = fetching;

  return (
    <Spin spinning={isLoading}>
      <div style={{ padding: "16px" }}>
        {fetchError && (
          <Alert
            type="error"
            showIcon
            message="Failed to load users and roles"
            description={fetchError}
            style={{ marginBottom: 16 }}
            closable
            onClose={() => setFetchError(null)}
          />
        )}
        {/* ROLES SECTION */}
        <DrawerRowDivider icon={faUserShield} title="Roles" />
        <div className={`drawer__box__${theme}`}>
          {sortedRoles.length === 0 && loaded && (
            <div className="row">
              <div className="col-12 mt-2">
                <Alert
                  type="info"
                  showIcon
                  message="No roles assigned yet. Click '+ Add Role' to assign a role to this organization."
                />
              </div>
            </div>
          )}
          <Table
            dataSource={sortedRoles}
            columns={roleColumns}
            pagination={false}
            size="small"
            locale={{ emptyText: "No roles assigned to this organization." }}
          />
          {/* Explains the star/shield beside every role name above. Renders the
              same marker components the table does, so it cannot drift. */}
          <RoleMarkerLegend />
        </div>
        <br />
        <div className={`drawer__box__${theme}`}>
          <div className="container">
            <div className="row">
              <div className="generalStyles__flexCenter" style={{ gap: 8, flexWrap: "wrap" }}>
                <WizardOpGate op="create">
                  <div>
                    <Button type="primary" size="large" onClick={handleOpenAddRole}>
                      + Add Role
                    </Button>
                  </div>
                  <div>
                    <Button
                      size="large"
                      onClick={() => {
                        setCreateRoleError(null);
                        createRoleForm.resetFields();
                        createRoleForm.setFieldsValue({ template: "VIEW_ONLY" });
                        // Seed the inline editor with the default template so
                        // the tree is editable before the first template click.
                        setCreateRoleMenu(templateMenu("VIEW_ONLY"));
                        setCreateRoleOpen(true);
                      }}
                      disabled={!templateCeiling}
                      loading={!templateCeiling && !isErrorPermissionCatalogue}
                      title="Create a brand new role for this org from a built-in permission template"
                    >
                      + Create New Role
                    </Button>
                  </div>
                </WizardOpGate>
              </div>
            </div>
          </div>
        </div>
        <br />

        {/* USERS SECTION */}
        <DrawerRowDivider icon={faUsers} title="Users" />
        <div className={`drawer__box__${theme}`}>
          {users.length === 0 && loaded && (
            <Alert
              type="info"
              showIcon
              message="No users yet. Click '+ Add User' to create one."
              style={{ marginBottom: 12 }}
            />
          )}
          <Table
            dataSource={users}
            columns={userColumns}
            pagination={false}
            size="small"
            locale={{ emptyText: "No users found for this organization." }}
          />
          {/* Legend for the Status column above. It is rendered from the
              generated UserStatus enum and shares its colours with the
              column, so it stays exhaustive and consistent by construction. */}
          <UserStatusLegend />
        </div>

        {/* Every dialog the actions menu above can open. onUsersChanged clears
            `loaded`, which re-runs the fetch effect: this step reads users
            through a LAZY query, so it has no tag invalidation to ride on and
            would otherwise show a deleted or newly blocked user until the
            operator left the step and came back. */}
        <UserCrudSurface onUsersChanged={() => setLoaded(false)} />
        {/* Shown once, immediately after an edge create returns a generated
            password. Clearing the state is what closes it, so the credential
            does not outlive the dialog. */}
        <OfflinePasswordHandoff
          open={handoffPassword !== ""}
          password={handoffPassword}
          userLabel={handoffUserLabel}
          onClose={() => {
            setHandoffPassword("");
            setHandoffUserLabel("");
          }}
        />
        <br />
        <div className={`drawer__box__${theme}`}>
          <div className="container">
            <div className="row">
              <div className="generalStyles__flexCenter">
                <WizardOpGate op="create">
                  <div>
                    <Button
                      type="primary"
                      size="large"
                      onClick={() => {
                        setEditingUserId(null);
                        setAddUserError(null);
                        userForm.resetFields();
                        setAddUserOpen(true);
                      }}
                    >
                      + Add User
                    </Button>
                  </div>
                </WizardOpGate>
              </div>
            </div>
          </div>
        </div>

        {/* ADD ROLE MODAL */}
        <Modal
          title="Add Role"
          open={addRoleOpen}
          width={720}
          onCancel={() => {
            setAddRoleOpen(false);
            setAddRoleError(null);
            setAddRoleMenu(null);
            setAddRoleMenuForId(null);
            setAddRoleScope(EMPTY_SCOPE);
            roleForm.resetFields();
          }}
          onOk={() => roleForm.submit()}
          okText="Add Role"
          confirmLoading={addRoleSaving}
          destroyOnClose
        >
          {addRoleError && (
            <Alert type="error" showIcon message={addRoleError} style={{ marginBottom: 12 }} />
          )}
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message="Select a base role to inherit its permissions, then fine-tune them below before creating. Optionally provide a display name specific to this organization."
          />
          <Form form={roleForm} layout="vertical" onFinish={handleAddRole}>
            <Form.Item
              label="Base Role"
              name="base_role_id"
              rules={[{ required: true, message: "Please select a base role" }]}
            >
              <Select
                placeholder="Select a role to inherit from"
                showSearch
                optionFilterProp="children"
                loading={baseRoles.length === 0 && addRoleOpen && !addRoleError}
                onChange={(value: string) => handleBaseRoleSelected(value)}
              >
                {baseRoles.map((r) => (
                  <Select.Option key={r.id} value={r.id}>
                    {r.name}
                    {r.orgName ? ` (${r.orgName})` : ""}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              label="Display Name (optional)"
              name="display_name"
              extra="Custom name for this role in this organization. Leave blank to use the base role name."
            >
              <Input placeholder="e.g. Manager, Building Admin, Technician" />
            </Form.Item>
            <Form.Item label="Permissions">
              <WizardPermissionTreeEditor
                menu={addRoleMenu}
                onChange={setAddRoleMenu}
                loading={addRoleMenuLoading}
              />
            </Form.Item>
            <Form.Item label={t("general.scopeEditorTitle")}>
              <RoleScopeEditor
                value={addRoleScope}
                onChange={setAddRoleScope}
                orgId={wizardState.organizationId!}
              />
            </Form.Item>
          </Form>
        </Modal>

        {/* CREATE-FROM-TEMPLATE MODAL */}
        <Modal
          title="Create New Role"
          open={createRoleOpen}
          width={720}
          onCancel={() => {
            setCreateRoleOpen(false);
            setCreateRoleError(null);
            setCreateRoleMenu(null);
            setCreateRoleScope(EMPTY_SCOPE);
            createRoleForm.resetFields();
          }}
          onOk={() => createRoleForm.submit()}
          okText="Create Role"
          confirmLoading={createRoleSaving}
          destroyOnClose
        >
          {createRoleError && (
            <Alert type="error" showIcon message={createRoleError} style={{ marginBottom: 12 }} />
          )}
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message="Create a brand new role from a built-in template, then fine-tune individual permissions below before creating. Switching templates resets the tree."
          />
          <Form
            form={createRoleForm}
            layout="vertical"
            onFinish={handleCreateRoleFromTemplate}
            initialValues={{ template: "VIEW_ONLY" }}
            onValuesChange={(changed: { template?: RoleTemplate }) => {
              // Template switch re-seeds the inline editor; granular
              // tweaks always start from the freshly chosen tier.
              if (changed.template) {
                setCreateRoleMenu(templateMenu(changed.template));
              }
            }}
          >
            <Form.Item
              label="Role Name"
              name="name"
              rules={[
                { required: true, message: "Role name is required" },
                { max: 50, message: "Role name must be 50 characters or fewer" },
              ]}
            >
              <Input placeholder="e.g. Master Admin, Admin, User" />
            </Form.Item>
            <Form.Item
              label="Display Name (optional)"
              name="display_name"
              extra="Optional alias shown to operators in this organization. Leave blank to use the role name."
            >
              <Input placeholder="e.g. Building Manager" />
            </Form.Item>
            <Form.Item
              label="Permission Template"
              name="template"
              rules={[{ required: true, message: "Please choose a template" }]}
            >
              <Radio.Group style={{ width: "100%" }}>
                {ROLE_TEMPLATES.map((tpl) => (
                  <Radio
                    key={tpl.value}
                    value={tpl.value}
                    style={{ display: "block", marginBottom: 8 }}
                  >
                    <strong>{tpl.label}</strong>
                    <div style={{ fontSize: 12, color: "#888", marginTop: 2 }}>
                      {tpl.description}
                    </div>
                  </Radio>
                ))}
              </Radio.Group>
            </Form.Item>
            <Form.Item label="Permissions">
              <WizardPermissionTreeEditor menu={createRoleMenu} onChange={setCreateRoleMenu} />
            </Form.Item>
            <Form.Item label={t("general.scopeEditorTitle")}>
              <RoleScopeEditor
                value={createRoleScope}
                onChange={setCreateRoleScope}
                orgId={wizardState.organizationId!}
              />
            </Form.Item>
          </Form>
        </Modal>

        {/* EDIT ROLE MODAL */}
        <Modal
          title="Edit Role"
          open={editRoleOpen}
          width={720}
          onCancel={() => {
            setEditRoleOpen(false);
            setEditRoleError(null);
            setEditingRoleId(null);
            setEditRoleMenu(null);
            setEditRoleScope(EMPTY_SCOPE);
            editRoleForm.resetFields();
          }}
          onOk={() => editRoleForm.submit()}
          okText="Save Changes"
          confirmLoading={editRoleSaving}
          okButtonProps={{ disabled: editRoleMenuLoading }}
          destroyOnClose
        >
          {editRoleError && (
            <Alert type="error" showIcon message={editRoleError} style={{ marginBottom: 12 }} />
          )}
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message="Edit the role name, active status, and granular permissions. Online flags gate the cloud-connected UI; Offline flags gate the edge-only UI."
          />
          <WizardReadOnlyId label="Role ID" value={editingRoleId} />
          <Form form={editRoleForm} layout="vertical" onFinish={handleEditRole}>
            <Form.Item
              label="Role Name"
              name="name"
              rules={[
                { required: true, message: "Role name is required" },
                { max: 50, message: "Role name must be 50 characters or fewer" },
              ]}
            >
              <Input placeholder="e.g. Building Manager" />
            </Form.Item>
            <Form.Item label="Active" name="active">
              <Radio.Group>
                <Radio value>Active</Radio>
                <Radio value={false}>Inactive</Radio>
              </Radio.Group>
            </Form.Item>
            <Form.Item label="Permissions">
              <WizardPermissionTreeEditor
                menu={editRoleMenu}
                onChange={setEditRoleMenu}
                loading={editRoleMenuLoading}
              />
            </Form.Item>
            <Form.Item label={t("general.scopeEditorTitle")}>
              <RoleScopeEditor
                value={editRoleScope}
                onChange={setEditRoleScope}
                orgId={wizardState.organizationId!}
              />
            </Form.Item>
          </Form>
        </Modal>

        {/* ADD / EDIT USER MODAL */}
        <Modal
          title={editingUserId ? "Edit User" : "Add User"}
          open={addUserOpen}
          onCancel={() => {
            setAddUserOpen(false);
            setAddUserError(null);
            setEditingUserId(null);
            setEditingUserCognitoId(null);
            userForm.resetFields();
          }}
          onOk={() => userForm.submit()}
          okText={editingUserId ? "Save Changes" : "Create User"}
          okButtonProps={{ disabled: editingUserId ? !access.update : !access.create }}
          confirmLoading={addUserSaving}
          destroyOnClose
        >
          {addUserError && (
            <Alert type="error" showIcon message={addUserError} style={{ marginBottom: 12 }} />
          )}
          {sortedRoles.length === 0 && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 12 }}
              message="No roles available. Add a role first before creating users."
            />
          )}
          <WizardReadOnlyId label="User ID" value={editingUserId} />
          {editingUserId && (
            <WizardReadOnlyId
              label="Cognito ID"
              value={editingUserCognitoId}
              emptyText="Not linked to a Cognito identity"
            />
          )}
          <Form form={userForm} layout="vertical" onFinish={handleAddUser}>
            <Form.Item label="First Name" name="first_name">
              <Input placeholder="First name" />
            </Form.Item>
            <Form.Item label="Middle Name" name="middle_name">
              <Input placeholder="Middle name" />
            </Form.Item>
            <Form.Item label="Last Name" name="last_name">
              <Input placeholder="Last name" />
            </Form.Item>
            <Form.Item
              label="Email"
              name="email"
              rules={[
                { required: true, message: "Email is required" },
                { type: "email", message: "Enter a valid email" },
              ]}
            >
              <Input placeholder="user@example.com" />
            </Form.Item>
            <Form.Item label="Phone Code" name="phone_code">
              <Input placeholder="e.g. 1" />
            </Form.Item>
            <Form.Item label="Phone" name="phone">
              <Input placeholder="Phone number" />
            </Form.Item>
            <Form.Item
              label="Role"
              name="role_id"
              rules={[{ required: true, message: "Role is required" }]}
            >
              <Select placeholder="Select a role">
                {sortedRoles.map((r) => (
                  <Select.Option key={r.id} value={r.id}>
                    {/* Same markers as the Roles table: the master star for a
                        MASTER role (owned by the LiftNet master organization),
                        the tenant shield for everything else — visually
                        distinct wherever an operator picks one. */}
                    <MasterRoleStar show={r.isMasterOrgRole} />
                    <TenantRoleShield show={!r.isMasterOrgRole} />
                    {r.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Form>
        </Modal>

        {/* EXPORT BUTTON */}
        <Divider />
        <div className={`drawer__box__${theme}`}>
          <div className="container">
            <div className="row">
              <div className="generalStyles__flexCenter">
                <div className="generalStyles__mrFix">
                  {/* Backend stage 8 = Users & Roles. Previously hardcoded as
                      7 (pre-ChannelModifiers numbering), which downloaded the
                      BACnet scope instead. */}
                  <WizardExportButton stage={8} orgId={wizardState.organizationId} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Spin>
  );
}
